import { Controller } from "@hotwired/stimulus"

// Auto-guardado de borradores (recursos y unidades). Va sobre el <form> y
// solo se monta para el autor de un borrador o de uno nuevo
// (FlujoEditorialHelper#autoguardado_activo?).
//
// Cada guardado manda únicamente los grupos que cambiaron desde el último:
// un campo, un arreglo (clases de la unidad) o una fila anidada completa
// (un link, un adjunto). El primero crea el borrador (POST) y desde ahí el
// formulario apunta a su PATCH. El servidor devuelve el id de cada fila
// anidada nueva y se inyecta en la fila, para que la próxima vez se
// actualice en vez de duplicarse.
const ESPERA = 1500 // ms sin cambios antes de guardar
const ESPERA_MAXIMA = 10000 // tope mientras se sigue escribiendo sin pausa
const REINTENTOS = 3
const LIMITE_KEEPALIVE = 60000 // bytes; el navegador corta en 64 KB
const IGNORADOS = new Set(["authenticity_token", "_method", "autoguardado"])

const tokenCsrf = () => document.querySelector("meta[name='csrf-token']")?.content

// Aviso al salir de la edición: lo muestra la página siguiente
// (aviso_diferido_controller), porque Turbo reemplaza el body.
export const AVISO_PENDIENTE = "acercar:aviso-pendiente"
const AVISO_BORRADOR = "Tu borrador quedó guardado. Puedes retomarlo cuando quieras desde"

const vacio = (valor) => (valor instanceof File ? valor.size === 0 && !valor.name : valor === "")

// Un input de archivo vacío llega como un File sin nombre fechado "ahora":
// sin normalizarlo, cambiaría en cada lectura.
const firmaDeValor = (valor) => {
  if (!(valor instanceof File)) return valor
  return vacio(valor) ? "archivo:" : `archivo:${valor.name}:${valor.size}:${valor.lastModified}`
}

export default class extends Controller {
  // boton: "Guardar borrador", que además muestra el estado del guardado.
  static targets = ["boton", "estado"]

  connect() {
    this.persistido = !!this.element.querySelector("input[name='_method']")
    this.modelo = [...this.element.elements].map((campo) => campo.name?.match(/^(\w+)\[/)?.[1]).find(Boolean)
    this.instantanea = this.firmas()
    this.huerfanas = [] // filas guardadas que el autor quitó antes de recibir su id
    this.enVuelo = null
    this.intentos = 0
    this.detenido = false
    this.horaGuardado = this.hasBotonTarget ? this.botonTarget.dataset.hora : null
    // Al salir con Turbo, location ya es la página siguiente: se recuerda esta.
    this.rutaEdicion = location.pathname

    this.alCambiar = (event) => this.cambio(event)
    this.alEnviar = (event) => this.enviar(event)
    this.alDescargar = (event) => this.descargar(event)
    this.alOcultar = () => { if (document.visibilityState === "hidden") this.guardar() }

    for (const tipo of ["input", "change", "nested-form:cambio", "ordered-list:cambio"]) {
      this.element.addEventListener(tipo, this.alCambiar)
    }
    this.element.addEventListener("submit", this.alEnviar)
    window.addEventListener("beforeunload", this.alDescargar)
    document.addEventListener("visibilitychange", this.alOcultar)
  }

  // Al salir con Turbo el documento sigue vivo: el fetch alcanza a terminar.
  disconnect() {
    for (const tipo of ["input", "change", "nested-form:cambio", "ordered-list:cambio"]) {
      this.element.removeEventListener(tipo, this.alCambiar)
    }
    this.element.removeEventListener("submit", this.alEnviar)
    window.removeEventListener("beforeunload", this.alDescargar)
    document.removeEventListener("visibilitychange", this.alOcultar)
    clearTimeout(this.temporizador)
    if (this.detenido) return

    const cambios = this.hayCambios()
    if (cambios) this.guardar()
    if (this.persistido || cambios) this.dejarAviso()
  }

  // ---------------------------------------------------------------- Disparo

  cambio(event) {
    // Buscadores y mini-formularios sin name (combobox, chips de media) no
    // son datos del formulario.
    if ((event.type === "input" || event.type === "change") && !event.target.name) return
    if (this.detenido) return

    this.pintar("pendiente")
    this.primerCambio ??= Date.now()
    clearTimeout(this.temporizador)
    const restante = Math.max(0, ESPERA_MAXIMA - (Date.now() - this.primerCambio))
    this.temporizador = setTimeout(() => this.guardar(), Math.min(ESPERA, restante))
  }

  // Botón "Guardar borrador": guarda en el momento lo pendiente y confirma
  // con un aviso dónde retomarlo.
  async guardarBorrador() {
    if (this.detenido) return
    this.intentos = 0
    if (!this.persistido && !this.hayCambios()) {
      return this.aviso("Escribe algo primero: el borrador se guarda apenas completes un campo.", { tipo: "alert" })
    }
    while (this.enVuelo) await this.enVuelo.catch(() => {})
    // Siempre por guardar(): sin cambios reales solo confirma el estado. (Al
    // hacer clic, el campo que se editaba pierde el foco y emite un "change"
    // que lo marcaba como pendiente aunque no hubiera nada nuevo.)
    await this.guardar().catch(() => {})
    while (this.enVuelo) await this.enVuelo.catch(() => {})
    if (this.botonTarget.dataset.estado === "guardado") this.aviso(AVISO_BORRADOR, { enlace: true })
  }

  // Enviar a revisión: primero se guarda lo pendiente (si el envío falla por
  // un campo obligatorio, el borrador ya tiene todo) y se espera el guardado
  // en vuelo, que puede ser el que crea el registro (sin esperar, el envío
  // crearía otro).
  async enviar(event) {
    if (this.detenido) return
    this.detenido = true
    clearTimeout(this.temporizador)
    if (!this.enVuelo && !this.hayCambios()) return

    event.preventDefault()
    const boton = event.submitter
    if (boton) boton.disabled = true
    while (this.enVuelo) await this.enVuelo.catch(() => {})
    if (this.hayCambios()) await this.guardar({ forzar: true }).catch(() => {})
    if (boton) boton.disabled = false
    this.element.requestSubmit(boton)
  }

  // Cerrar o recargar la pestaña: si cabe, el guardado viaja con keepalive
  // (sobrevive a la descarga); si no, el navegador pregunta antes de salir.
  descargar(event) {
    if (this.detenido) return
    const cambios = this.hayCambios()
    if (!cambios && !this.enVuelo) return

    if (cambios && this.persistido && !this.enVuelo) {
      const { datos, peso } = this.datosDeCambios()
      if (peso < LIMITE_KEEPALIVE) {
        fetch(this.element.action, { method: "POST", body: datos, keepalive: true, credentials: "same-origin",
                                     headers: { "X-CSRF-Token": tokenCsrf(), Accept: "application/json" } })
        this.dejarAviso()
        return
      }
    }
    if (cambios) this.guardar()
    event.preventDefault()
    event.returnValue = ""
  }

  // ---------------------------------------------------------------- Guardado

  async guardar({ forzar = false } = {}) {
    if (this.detenido && !forzar) return
    if (this.enVuelo) {
      this.pendiente = true
      return this.enVuelo
    }
    clearTimeout(this.temporizador)
    this.primerCambio = null

    const { datos, enviados } = this.datosDeCambios()
    if (enviados.size === 0 && this.huerfanas.length === 0) {
      this.pintar(this.persistido ? "guardado" : "inicial")
      return
    }

    this.enVuelo = this.solicitar(datos, enviados).finally(() => {
      this.enVuelo = null
      if (this.pendiente && !this.detenido) {
        this.pendiente = false
        this.guardar()
      }
    })
    return this.enVuelo
  }

  async solicitar(datos, enviados) {
    this.pintar("guardando")
    const huerfanas = this.huerfanas
    this.huerfanas = []

    let respuesta
    try {
      respuesta = await fetch(this.element.action, {
        method: "POST",
        body: datos,
        credentials: "same-origin",
        headers: { "X-CSRF-Token": tokenCsrf(), Accept: "application/json" }
      })
    } catch {
      this.huerfanas.push(...huerfanas)
      return this.reintentar("Sin conexión")
    }

    if ([403, 404, 409].includes(respuesta.status)) {
      this.detenido = true
      return this.pintar("detenido", "Este contenido ya no es un borrador: recarga la página.")
    }
    if (respuesta.status === 422) {
      this.huerfanas.push(...huerfanas)
      const { errores = [] } = await respuesta.json().catch(() => ({}))
      return this.pintar("error", `No se pudo guardar: ${errores[0] || "revisa los datos"}`)
    }
    if (!respuesta.ok) {
      this.huerfanas.push(...huerfanas)
      return this.reintentar("Error del servidor")
    }

    const json = await respuesta.json().catch(() => null)
    if (!json) return this.reintentar("Respuesta inesperada")
    this.aplicar(json, enviados)
    this.intentos = 0
    this.horaGuardado = new Date(json.guardado_en).toLocaleTimeString("es-CL",
      { hour: "2-digit", minute: "2-digit", hourCycle: "h23" })
    this.pintar(this.hayCambios() ? "pendiente" : "guardado")
  }

  reintentar(motivo) {
    this.intentos += 1
    if (this.intentos > REINTENTOS) {
      return this.pintar("error", `${motivo}: no se pudo guardar`)
    }
    this.pintar("reintentando", `${motivo}: reintentando…`)
    clearTimeout(this.temporizador)
    this.temporizador = setTimeout(() => this.guardar(), 2000 * this.intentos)
  }

  // Lo que el servidor devolvió: la URL del borrador (primer guardado) y los
  // ids de las filas nuevas. La instantánea avanza a lo enviado; si el grupo
  // no cambió mientras viajaba, a su estado actual (con el id inyectado y el
  // archivo ya subido fuera del input).
  aplicar(json, enviados) {
    const antes = this.firmas()

    if (!this.persistido) {
      this.persistido = true
      this.element.action = json.url_actualizar
      const metodo = document.createElement("input")
      Object.assign(metodo, { type: "hidden", name: "_method", value: "patch" })
      this.element.prepend(metodo)
      // El token del form era para el POST de creación (tokens por formulario).
      const token = this.element.querySelector("input[name='authenticity_token']")
      if (token && tokenCsrf()) token.value = tokenCsrf()
      if (this.element.isConnected) {
        history.replaceState(history.state, "", json.url_editar)
        this.rutaEdicion = location.pathname
      }
    }

    for (const [asociacion, ids] of Object.entries(json.filas || {})) {
      for (const [clave, id] of Object.entries(ids)) this.inyectarId(asociacion, clave, id)
    }

    for (const campo of this.element.querySelectorAll("input[type='file']")) {
      const archivo = campo.files?.[0]
      const enviado = enviados.get(this.claveDeGrupo(campo.name))
      if (archivo && enviado?.includes(JSON.stringify(firmaDeValor(archivo)))) campo.value = ""
    }

    // Una fila guardada con _destroy ya no existe: si siguiera en el
    // formulario, el envío final pediría borrarla de nuevo (RecordNotFound).
    for (const [clave, firma] of [...enviados]) {
      if (!firma.includes('[_destroy]","1"]')) continue
      for (const elemento of [...this.element.elements].filter((e) => e.name?.startsWith(`${clave}[`))) {
        (elemento.closest("[data-nested-form-target='item']") || elemento).remove()
      }
      enviados.delete(clave)
      this.instantanea.delete(clave)
    }

    const despues = this.firmas()
    for (const [clave, firma] of enviados) {
      this.instantanea.set(clave, antes.get(clave) === firma && despues.has(clave) ? despues.get(clave) : firma)
    }
  }

  inyectarId(asociacion, clave, id) {
    const prefijo = `${this.modelo}[${asociacion}_attributes][${clave}]`
    const campo = [...this.element.elements].find((elemento) => elemento.name?.startsWith(`${prefijo}[`))
    const fila = campo?.closest("[data-nested-form-target='item']")
    if (!fila) {
      this.huerfanas.push({ prefijo, id })
      return
    }

    for (const [nombre, valor] of [["id", id], ["_destroy", "false"]]) {
      if (this.element.querySelector(`input[name='${prefijo}[${nombre}]']`)) continue
      const oculto = document.createElement("input")
      Object.assign(oculto, { type: "hidden", name: `${prefijo}[${nombre}]`, value: String(valor) })
      fila.appendChild(oculto)
    }
  }

  // ---------------------------------------------------------------- Grupos

  // Fila anidada (resource[links_attributes][12]) → viaja completa.
  claveDeGrupo(nombre) {
    return nombre.match(/^.+?_attributes\]\[[^\]]+\]/)?.[0] || nombre
  }

  grupos() {
    const grupos = new Map()
    for (const [nombre, valor] of new FormData(this.element)) {
      if (IGNORADOS.has(nombre)) continue
      const clave = this.claveDeGrupo(nombre)
      if (!grupos.has(clave)) grupos.set(clave, [])
      grupos.get(clave).push([nombre, valor])
    }
    return grupos
  }

  firmas(grupos = this.grupos()) {
    const firmas = new Map()
    for (const [clave, entradas] of grupos) {
      firmas.set(clave, JSON.stringify(entradas.map(([nombre, valor]) => [nombre, firmaDeValor(valor)])))
    }
    return firmas
  }

  cambiados() {
    const grupos = this.grupos()
    const firmas = this.firmas(grupos)
    return [...grupos].filter(([clave, entradas]) => {
      // Una fila recién agregada y todavía vacía no es un cambio.
      if (!this.instantanea.has(clave) && entradas.every(([, valor]) => vacio(valor))) return false
      return firmas.get(clave) !== this.instantanea.get(clave)
    }).map(([clave, entradas]) => [clave, entradas, firmas.get(clave)])
  }

  hayCambios() {
    return this.huerfanas.length > 0 || this.cambiados().length > 0
  }

  datosDeCambios() {
    const datos = new FormData()
    const enviados = new Map()
    let peso = 0
    datos.append("autoguardado", "1")
    if (this.persistido) datos.append("_method", "patch")

    for (const [clave, entradas, firma] of this.cambiados()) {
      for (const [nombre, valor] of entradas) {
        datos.append(nombre, valor)
        peso += nombre.length + (valor instanceof File ? valor.size : valor.length)
      }
      enviados.set(clave, firma)
    }
    for (const { prefijo, id } of this.huerfanas) {
      datos.append(`${prefijo}[id]`, id)
      datos.append(`${prefijo}[_destroy]`, "1")
    }
    return { datos, enviados, peso }
  }

  // ---------------------------------------------------------------- Indicador

  // El botón siempre dice "Guardar borrador"; el estado va a la izquierda
  // de la barra: la hora del último guardado, o qué está pasando.
  pintar(estado, detalle = "") {
    if (!this.hasBotonTarget) return
    const ultimo = this.horaGuardado ? `Guardado a las ${this.horaGuardado}` : "Aún sin guardar"
    const textos = {
      inicial: ultimo,
      pendiente: ultimo,
      guardado: ultimo,
      guardando: "Guardando…",
      reintentando: "Reintentando…",
      error: "No se pudo guardar",
      detenido: "No se pudo guardar"
    }
    const conProblema = ["error", "detenido"].includes(estado)
    this.botonTarget.dataset.estado = estado
    this.botonTarget.disabled = ["guardando", "reintentando", "detenido"].includes(estado)
    this.estadoTarget.textContent = textos[estado]
    this.estadoTarget.classList.toggle("text-danger", conProblema)
    this.estadoTarget.classList.toggle("text-dim", !conProblema)
    this.estadoTarget.title = detalle
  }

  // ---------------------------------------------------------------- Avisos

  // Aviso en pantalla, con el mismo aspecto de los flash del sitio.
  aviso(texto, { tipo = "notice", enlace = false } = {}) {
    mostrarAviso({ texto, tipo, enlace })
  }

  // La página siguiente lo muestra (salvo que sea esta misma, al recargar).
  dejarAviso() {
    try {
      sessionStorage.setItem(AVISO_PENDIENTE,
        JSON.stringify({ texto: AVISO_BORRADOR, enlace: true, desde: this.rutaEdicion }))
    } catch { /* sin almacenamiento (modo privado): no hay aviso */ }
  }
}

// Compartida con aviso_diferido_controller.
export function mostrarAviso({ texto, tipo = "notice", enlace = false }) {
  const contenedor = document.getElementById("flash")
  if (!contenedor) return

  const estilos = {
    notice: "border-green/30 bg-green/10 text-green",
    alert: "border-gold/40 bg-gold/10 text-gold-dark"
  }
  const aviso = document.createElement("div")
  aviso.className = `pointer-events-auto flex w-full max-w-md items-start gap-3 rounded-2xl border bg-white px-4 py-3 shadow-float transition duration-200 ${estilos[tipo] || estilos.notice}`
  aviso.setAttribute("role", "status")
  aviso.dataset.controller = "flash"
  aviso.dataset.flashDismissAfterValue = "7000"

  // Mismo ícono que los flash del servidor (check o aviso).
  const trazo = tipo === "alert"
    ? '<path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12Z"/><circle cx="12" cy="12" r="2.5"/>'
    : '<path d="M5 13l4 4L19 7"/>'
  aviso.insertAdjacentHTML("beforeend", `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" ` +
    `stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" ` +
    `class="mt-0.5 flex-none" aria-hidden="true">${trazo}</svg>`)

  const parrafo = document.createElement("p")
  parrafo.className = "flex-1 font-sans text-[15px] font-semibold"
  parrafo.append(texto)
  if (enlace) {
    const link = document.createElement("a")
    link.href = "/mis-recursos"
    link.className = "underline underline-offset-2"
    link.textContent = "Mis recursos"
    parrafo.append(" ", link, ".")
  }
  const cerrar = document.createElement("button")
  cerrar.type = "button"
  cerrar.className = "text-current opacity-60 transition hover:opacity-100"
  cerrar.dataset.action = "flash#dismiss"
  cerrar.setAttribute("aria-label", "Cerrar")
  cerrar.textContent = "×"
  aviso.append(parrafo, cerrar)
  contenedor.appendChild(aviso)
}
